import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { cognitoApi, decodeIdToken, hostedUi, createPkcePair } from '../api/cognitoApi';
import { buildPermissions, normalizePermissions } from '../config/rbac';
import { accessOfRecord } from '../config/access';

// ─── Token storage ────────────────────────────────────────────────────────────
// Stored in localStorage so a refresh keeps the user signed in. The id token's
// signature is verified server-side; the browser just trusts what's there.

const STORAGE_KEY = 'fce.auth';

function readStored() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.idToken) return null;
        return parsed;
    } catch {
        return null;
    }
}

function writeStored(value) {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else       localStorage.removeItem(STORAGE_KEY);
}

// ─── Hosted-UI (Google) redirect bookkeeping ──────────────────────────────────
// One sessionStorage entry per in-flight redirect, keyed by the OAuth `state`
// nonce: the PKCE verifier + where to land afterwards. Entries older than 10
// minutes are ignored (a stale tab coming back much later shouldn't sign in).

const OAUTH_KEY_PREFIX = 'fce.oauth.';
const OAUTH_TTL_MS     = 10 * 60 * 1000;

function rememberOAuthAttempt(state, entry) {
    try { sessionStorage.setItem(OAUTH_KEY_PREFIX + state, JSON.stringify({ ...entry, at: Date.now() })); } catch { /* private mode */ }
}

function takeOAuthAttempt(state) {
    if (!state) return null;
    const key = OAUTH_KEY_PREFIX + state;
    try {
        const raw = sessionStorage.getItem(key);
        sessionStorage.removeItem(key);
        if (!raw) return null;
        const entry = JSON.parse(raw);
        if (!entry?.at || Date.now() - entry.at > OAUTH_TTL_MS) return null;
        return entry;
    } catch {
        return null;
    }
}

// The redirect_uri must be byte-identical between /oauth2/authorize and
// /oauth2/token and match a registered callback URL exactly (origin + /login).
const OAUTH_CALLBACK_PATH = '/login';
function oauthRedirectUri() {
    return `${window.location.origin}${OAUTH_CALLBACK_PATH}`;
}

// Cognito's PreSignUp trigger links a first-time Google sign-in to the existing
// email+password account. Cognito can still bounce that very first attempt with
// this message; one automatic retry lands on the linked account.
const LINK_RETRY_RE  = /already found an entry for username/i;
const LINK_RETRY_KEY = 'fce.oauth.linkRetry';

// Cognito's error_description strings → what a person should read.
function friendlyOAuthError(description) {
    const text = String(description || '').replace(/^PreSignUp failed with error\s*/i, '').trim();
    if (!text || /access_denied|cancell?ed/i.test(text)) {
        return 'Google sign-in was cancelled. You can try again or continue with your work email.';
    }
    if (/attributes required/i.test(text)) {
        return 'Your Google account doesn’t share a first and last name, so we couldn’t create your profile. Continue with your work email below.';
    }
    if (/login option is not available/i.test(text)) {
        return 'Google sign-in isn’t available for this workspace yet. Continue with your work email below.';
    }
    if (LINK_RETRY_RE.test(text)) {
        return 'We couldn’t link your Google account to your existing sign-in. Continue with your work email below.';
    }
    return text.replace(/\.$/, '') + '.';
}

// What's in the address bar right now, if it's a hosted-UI return leg.
function readOAuthReturnParams() {
    if (window.location.pathname !== OAUTH_CALLBACK_PATH) return null;
    const params = new URLSearchParams(window.location.search);
    if (!params.get('code') && !params.get('error')) return null;
    return params;
}

function profileFromIdToken(idToken) {
    const claims = decodeIdToken(idToken);
    if (!claims) return null;
    return {
        email:       claims.email     || '',
        firstName:   claims.given_name  || '',
        lastName:    claims.family_name || '',
        businessId:  claims['custom:business_id'] || '',
        // Cognito groups → role. If you put a user in the `admin` group, claims['cognito:groups'] = ['admin'].
        role:        Array.isArray(claims['cognito:groups']) ? claims['cognito:groups'][0] : 'admin',
        sub:         claims.sub,
        exp:         claims.exp,
        // Signed in through Google: Cognito copies the name from Google on
        // every such sign-in, so a name typed here would not stick.
        federated:   Array.isArray(claims.identities) && claims.identities.length > 0,
    };
}

// ─── Context ───────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [session, setSession] = useState(() => {
        const stored = readStored();
        if (!stored) return null;
        const profile = profileFromIdToken(stored.idToken);
        if (!profile) { writeStored(null); return null; }
        return { ...stored, profile };
    });

    // Pending NEW_PASSWORD_REQUIRED challenge from a first-time admin-created user.
    const [pendingChallenge, setPendingChallenge] = useState(null);

    // Impersonation, the org owner (admin) can "Switch to User" from a
    // collaborator detail page. We override the displayed user profile but
    // KEEP the original Cognito tokens (impersonation is UI-side; API calls
    // still go through the admin's session). A separate banner makes the
    // current state obvious so it's not abused.
    const [impersonatedUser, setImpersonatedUser] = useState(null);

    const isAuthenticated = !!session?.idToken;
    const realUser        = session?.profile || null;

    // Effective user. When impersonating, return the impersonated profile so
    // the rest of the app gates UI based on that user's permissions instead of
    // the admin's. Permissions on the user object are the single source of
    // truth, Sidebar / CollaboratorDetail / page guards read from here.
    const user = (() => {
        if (impersonatedUser) {
            return {
                ...impersonatedUser,
                permissions: normalizePermissions(impersonatedUser.permissions)
                    || buildPermissions(impersonatedUser.role || 'viewer'),
            };
        }
        if (!realUser) return null;
        // Org owner (Cognito admin) gets the admin preset by default.
        return {
            ...realUser,
            permissions: buildPermissions(realUser.role || 'viewer'),
        };
    })();

    const login = useCallback(async (email, password) => {
        const username = email.trim().toLowerCase();
        try {
            const res = await cognitoApi.initiateAuth(username, password);

            // First-login challenge from an admin-created user, caller must finish
            // by calling completeNewPassword(newPassword).
            if (res.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                setPendingChallenge({ username, session: res.Session });
                return { ok: false, challenge: 'NEW_PASSWORD_REQUIRED' };
            }

            const auth = res.AuthenticationResult;
            if (!auth?.IdToken) return { ok: false, error: 'Unexpected response from Cognito.' };

            const next = {
                idToken:      auth.IdToken,
                accessToken:  auth.AccessToken,
                refreshToken: auth.RefreshToken,
                profile:      profileFromIdToken(auth.IdToken),
            };
            writeStored(next);
            setSession(next);
            return { ok: true };
        } catch (err) {
            // Cognito returns "NotAuthorizedException" for bad creds and "UserNotConfirmedException"
            // for unverified accounts. Map both to a friendly message.
            const code = err.code || '';
            if (code.includes('NotAuthorized') || code.includes('UserNotFound')) {
                return { ok: false, error: 'Incorrect email or password.' };
            }
            if (code.includes('PasswordResetRequired')) {
                return { ok: false, error: 'Password reset required. Use Forgot password.' };
            }
            return { ok: false, error: err.message || 'Sign-in failed.' };
        }
    }, []);

    // The Profile page's name edit (owner, 2026-10-10: "make it possible to
    // change name …"). Cognito holds the name, so it is written there with the
    // person's own access token; the ID token every page reads the name from is
    // then fetched again so the new name shows at once. The freshest tokens are
    // in storage (utils/authToken refreshes them there), not in this state.
    const updateName = useCallback(async (firstName, lastName) => {
        const stored = readStored();
        if (!stored?.refreshToken) throw new Error('Sign in again to change your name.');
        const refreshed = async () => {
            const res  = await cognitoApi.refresh(stored.refreshToken);
            const auth = res.AuthenticationResult || {};
            if (!auth.IdToken) throw new Error('Sign in again to change your name.');
            return auth;
        };
        try {
            const before = await refreshed();
            await cognitoApi.updateUserAttributes(before.AccessToken, [
                { Name: 'given_name',  Value: String(firstName || '').trim() },
                { Name: 'family_name', Value: String(lastName || '').trim() },
            ]);
            const after = await refreshed();
            const next = {
                ...(readStored() || stored),
                idToken:     after.IdToken,
                accessToken: after.AccessToken || before.AccessToken,
                profile:     profileFromIdToken(after.IdToken),
            };
            writeStored(next);
            setSession(next);
            return next.profile;
        } catch (err) {
            const code = String(err.code || '');
            if (code.includes('NotAuthorized')) {
                throw new Error('Your sign-in can\'t change the name here. Sign out and back in with your email and password, then try again.');
            }
            throw new Error(err.message || 'Could not save your name.');
        }
    }, []);

    const completeNewPassword = useCallback(async (newPassword) => {
        if (!pendingChallenge) return { ok: false, error: 'No pending challenge.' };
        try {
            const res  = await cognitoApi.respondToNewPasswordChallenge(
                pendingChallenge.username,
                newPassword,
                pendingChallenge.session,
            );
            const auth = res.AuthenticationResult;
            if (!auth?.IdToken) return { ok: false, error: 'Unexpected response from Cognito.' };

            const next = {
                idToken:      auth.IdToken,
                accessToken:  auth.AccessToken,
                refreshToken: auth.RefreshToken,
                profile:      profileFromIdToken(auth.IdToken),
            };
            writeStored(next);
            setSession(next);
            setPendingChallenge(null);
            return { ok: true };
        } catch (err) {
            return { ok: false, error: err.message || 'Could not set new password.' };
        }
    }, [pendingChallenge]);

    // ── "Continue with Google" (hosted UI, authorization code + PKCE) ─────────
    // Step 1: leave the app for Cognito → Google. `from` is where to land once
    // we're back (restored from the state entry, location.state doesn't
    // survive a full-page redirect).
    const loginWithGoogle = useCallback(async ({ from = '/' } = {}) => {
        if (!hostedUi.enabled) {
            return { ok: false, error: 'Google sign-in isn’t enabled yet. Continue with your work email below.' };
        }
        try {
            const state = hostedUi.newState();
            const pkce  = await createPkcePair();
            rememberOAuthAttempt(state, { verifier: pkce.verifier, from, provider: 'google' });
            window.location.assign(hostedUi.authorizeUrl({
                identityProvider:    'Google',
                redirectUri:         oauthRedirectUri(),
                state,
                codeChallenge:       pkce.challenge,
                codeChallengeMethod: pkce.method,
            }));
            return { ok: true, redirecting: true };
        } catch (err) {
            return { ok: false, error: err.message || 'Could not start Google sign-in.' };
        }
    }, []);

    // Step 2: back on /login?code=…&state=…, trade the code for tokens and open
    // the session exactly like a password sign-in. Resolves { ok, from } or
    // { ok: false, error }.
    const completeHostedSignIn = useCallback(async ({ code, state }) => {
        const attempt = takeOAuthAttempt(state);
        if (!attempt) {
            return { ok: false, error: 'That Google sign-in link has expired. Please try again.' };
        }
        try {
            const tokens  = await hostedUi.exchangeCode({
                code,
                redirectUri:  oauthRedirectUri(),
                codeVerifier: attempt.verifier,
            });
            const profile = profileFromIdToken(tokens.id_token);
            if (!profile) return { ok: false, error: 'Unexpected response from Cognito.' };

            const next = {
                idToken:      tokens.id_token,
                accessToken:  tokens.access_token,
                refreshToken: tokens.refresh_token,
                profile,
                // Remembered so logout can also end the hosted-UI session.
                federated:    true,
                provider:     attempt.provider || 'google',
            };
            writeStored(next);
            setSession(next);
            setPendingChallenge(null);
            return { ok: true, from: attempt.from || '/' };
        } catch (err) {
            if (err.code === 'invalid_grant') {
                return { ok: false, error: 'That Google sign-in link was already used. Please try again.' };
            }
            return { ok: false, error: err.message || 'Google sign-in failed.' };
        }
    }, []);

    // ── Return leg: /login?code=…&state=…  or  /login?error=…&error_description=… ──
    // Handled HERE rather than in the login page because everything under
    // BusinessProvider is re-keyed (unmounted + remounted) the moment the
    // organizations list resolves, a page-level effect would lose its
    // in-flight exchange and state. AuthProvider mounts once, so this runs once.
    // The login page reads `oauthReturn` to show progress / the error, and
    // performs the navigation when status is 'done'.
    //   { status: 'idle' | 'busy' | 'done' | 'error', from?, error? }
    const [oauthReturn, setOauthReturn] = useState(() => (
        readOAuthReturnParams()?.get('code') ? { status: 'busy' } : { status: 'idle' }
    ));
    const clearOAuthReturn = useCallback(() => setOauthReturn({ status: 'idle' }), []);
    const oauthReturnHandled = useRef(false);

    useEffect(() => {
        const params = readOAuthReturnParams();
        if (!params) return;
        // The code is single-use: run exactly once, also across React StrictMode's
        // dev-only effect double-invoke (the ref survives it).
        if (oauthReturnHandled.current) return;
        oauthReturnHandled.current = true;

        (async () => {
            const code = params.get('code');
            if (code) {
                setOauthReturn({ status: 'busy' });
                const res = await completeHostedSignIn({ code, state: params.get('state') });
                if (res.ok) {
                    sessionStorage.removeItem(LINK_RETRY_KEY);
                    setOauthReturn({ status: 'done', from: res.from || '/' });
                } else {
                    setOauthReturn({ status: 'error', error: res.error || 'Google sign-in failed.' });
                }
                return;
            }

            // Cognito sent us back with an error (state still identifies the attempt).
            const description = params.get('error_description') || '';
            const attempt     = takeOAuthAttempt(params.get('state'));
            if (LINK_RETRY_RE.test(description) && !sessionStorage.getItem(LINK_RETRY_KEY)) {
                // First-time account link, go straight back; the second pass signs in.
                sessionStorage.setItem(LINK_RETRY_KEY, '1');
                setOauthReturn({ status: 'busy' });
                const res = await loginWithGoogle({ from: attempt?.from || '/' });
                if (!res.ok) setOauthReturn({ status: 'error', error: res.error });
                return;
            }
            sessionStorage.removeItem(LINK_RETRY_KEY);
            setOauthReturn({ status: 'error', error: friendlyOAuthError(description || params.get('error')) });
        })();
    }, [completeHostedSignIn, loginWithGoogle]);

    const logout = useCallback(() => {
        // Best-effort server-side revoke; ignore errors.
        if (session?.accessToken) cognitoApi.globalSignOut(session.accessToken).catch(() => {});
        const wasFederated = !!session?.federated;
        writeStored(null);
        setSession(null);
        setPendingChallenge(null);
        setImpersonatedUser(null);
        // A Google session also lives as a cookie on the hosted-UI domain; end it
        // there too or the next "Continue with Google" resumes this person silently.
        if (wasFederated && hostedUi.enabled) {
            window.location.assign(hostedUi.logoutUrl(oauthRedirectUri()));
        }
    }, [session]);

    const impersonate = useCallback((targetProfile) => {
        if (!targetProfile?.email) return;
        const role = targetProfile.role || 'viewer';
        setImpersonatedUser({
            email:        String(targetProfile.email || '').toLowerCase(),
            firstName:    targetProfile.firstName || targetProfile.first_name || '',
            lastName:     targetProfile.lastName  || targetProfile.last_name  || '',
            role,
            sub:          targetProfile.sub || targetProfile.id || targetProfile.user_id,
            businessId:   targetProfile.businessId || targetProfile.business_id || '',
            // ⤵ Crucial, without this, impersonation didn't actually enforce RBAC.
            permissions:  normalizePermissions(targetProfile.permissions) || buildPermissions(role),
            // Page-level grants (config/access.js), what usePermissions gates on.
            access:       accessOfRecord(targetProfile),
        });
    }, []);

    const stopImpersonating = useCallback(() => setImpersonatedUser(null), []);

    const forgotPassword = useCallback(async (email) => {
        try {
            await cognitoApi.forgotPassword(email.trim().toLowerCase());
            return { ok: true };
        } catch (err) {
            // For safety, treat "user not found" as success so we don't leak account existence.
            const code = err.code || '';
            if (code.includes('UserNotFound')) return { ok: true };
            return { ok: false, error: err.message || 'Could not start password reset.' };
        }
    }, []);

    const confirmForgotPassword = useCallback(async (email, code, newPassword) => {
        try {
            await cognitoApi.confirmForgotPassword(email.trim().toLowerCase(), code.trim(), newPassword);
            return { ok: true };
        } catch (err) {
            const c = err.code || '';
            if (c.includes('CodeMismatch'))  return { ok: false, error: 'That code is incorrect.' };
            if (c.includes('ExpiredCode'))   return { ok: false, error: 'That code expired. Please request a new one.' };
            if (c.includes('InvalidPassword')) return { ok: false, error: 'Password does not meet the policy (min 8, upper, lower, number).' };
            return { ok: false, error: err.message || 'Could not reset password.' };
        }
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            realUser,
            impersonatedUser,
            isImpersonating: !!impersonatedUser,
            isAuthenticated,
            idToken: session?.idToken || null,
            accessToken: session?.accessToken || null,
            login,
            loginWithGoogle,
            googleSignInEnabled: hostedUi.enabled,
            oauthReturn,
            clearOAuthReturn,
            logout,
            pendingChallenge,
            completeNewPassword,
            updateName,
            forgotPassword,
            confirmForgotPassword,
            impersonate,
            stopImpersonating,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
